/**
 * @fileoverview The Opportunities map (Leaflet + CARTO tiles).
 *
 * **Why Leaflet and not Mapbox/Google.** No API key and no billing account, so
 * a map never becomes a credential to rotate or an invoice to watch. CARTO's
 * basemaps are the one free tile set that sits inside Studio Quiet instead of
 * glowing white in the middle of it — `dark_all` under the dark theme, and
 * `light_all` under the light one, so the map is never the one panel painted
 * for the other ground. The tile layer is keyed on the theme so a switch
 * remounts it: Leaflet takes the URL template at construction and does not
 * re-read it on a prop change.
 *
 * Three things worth knowing before editing:
 *
 * 1. **Leaflet's default marker icons are broken under a bundler.** It builds
 *    icon URLs by string-concatenating against a path that only exists in its
 *    own dist layout, so markers silently render as broken images. We draw our
 *    own `divIcon` instead — which also means the pins are token-coloured
 *    rather than Leaflet blue.
 * 2. **The map is imperative below React.** `useMap` + an effect re-fits the
 *    view when the result set changes; doing it declaratively fights Leaflet
 *    for control of the viewport and the map ends up jumping on every render.
 * 3. **The container needs an explicit height.** Leaflet measures its parent,
 *    and a flex/auto height resolves to 0 — the map then renders as a grey
 *    sliver with no error.
 */

import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import useTheme from "@/hooks/useTheme";
import { boundsFor, hasCoords, distanceLabel, matchingInstruments } from "@/lib/geo";

/**
 * The accent, read from the design token at runtime.
 *
 * The radius circle can't take a CSS class: react-leaflet doesn't forward
 * `pathOptions.className` to Leaflet, so the ring silently renders in Leaflet's
 * default blue. Reading the custom property keeps the colour defined once, in
 * index.css, rather than copying its hex in here.
 */
function accentColor() {
  if (typeof window === "undefined") return undefined;
  return getComputedStyle(document.documentElement).getPropertyValue("--accent-yellow").trim() || undefined;
}

/** Fallback view when there is nothing to frame — the continental US. */
const DEFAULT_CENTER = [39.5, -98.35];
const DEFAULT_ZOOM = 4;

const MILES_TO_METRES = 1609.34;

/** Vertical room reserved above the topmost pin so its hover card isn't clipped. */
const TOOLTIP_HEADROOM_PX = 110;

/**
 * Token-coloured pins. A `divIcon` rather than an image because Leaflet's
 * bundled PNGs don't survive bundling (see the note above) — and the colour
 * comes from a CSS class, not an inline hex, so the accent lives only in the
 * token (see the Leaflet block in index.css).
 */
function pin(modifier, size = 16) {
  return L.divIcon({
    className: "",
    html: `<span class="hans-map-pin ${modifier}" style="width:${size}px;height:${size}px"></span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

const PROGRAM_PIN = pin("");
const ORIGIN_PIN = pin("hans-map-pin--origin", 12);

/**
 * Keeps the viewport on the current results.
 *
 * Imperative on purpose: Leaflet owns the viewport, so re-fitting from an
 * effect is the only way to change it without fighting the library for control
 * on every render.
 */
function FitBounds({ points, origin }) {
  const map = useMap();
  const key = useMemo(
    () => points.map((p) => `${p.latitude},${p.longitude}`).join("|") + `#${origin?.latitude ?? ""}`,
    [points, origin],
  );

  useEffect(() => {
    const framed = origin && hasCoords(origin) ? [...points, origin] : points;
    const bounds = boundsFor(framed);
    if (!bounds) {
      map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      return;
    }
    // A single point has zero-area bounds, which fitBounds resolves to maximum
    // zoom — a street view of one pin. Cap it at neighbourhood level.
    //
    // The VERTICAL padding is deliberately much larger than the horizontal:
    // hover cards open above their pin, and the container clips them (it has
    // overflow:hidden for its rounded corners), so a pin fitted near the top
    // edge loses the program title — the one line that matters most. This
    // reserves roughly a card's height above the topmost pin.
    map.fitBounds(bounds, { padding: [40, TOOLTIP_HEADROOM_PX], maxZoom: 11 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, map]);

  return null;
}

export default function ProgramsMap({ programs, origin, radius, onSelect, teaches, height = 420 }) {
  const located = (programs || []).filter(hasCoords);
  const [theme] = useTheme();

  return (
    // Leaflet measures its parent, so the height has to be explicit — an auto
    // height collapses to 0 and the map renders as a grey sliver.
    <div
      className="overflow-hidden rounded-[20px] border border-[var(--studio-divider)]"
      style={{ height }}
      data-testid="programs-map"
    >
      <MapContainer
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        scrollWheelZoom
        style={{ height: "100%", width: "100%", background: "var(--studio-raised)" }}
      >
        <TileLayer
          key={theme}
          url={`https://{s}.basemaps.cartocdn.com/${theme === "light" ? "light_all" : "dark_all"}/{z}/{x}/{y}{r}.png`}
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <FitBounds points={located} origin={origin} />

        {origin && hasCoords(origin) && (
          <>
            <Marker position={[origin.latitude, origin.longitude]} icon={ORIGIN_PIN}>
              <Tooltip direction="top" offset={[0, -8]}>
                You&apos;re near {origin.place}
              </Tooltip>
            </Marker>
            {radius && (
              <Circle
                center={[origin.latitude, origin.longitude]}
                radius={radius * MILES_TO_METRES}
                pathOptions={{ color: accentColor(), weight: 1, opacity: 0.35, fillOpacity: 0.05 }}
              />
            )}
          </>
        )}

        <ProgramMarkers programs={located} teaches={teaches} onSelect={onSelect} />
      </MapContainer>
    </div>
  );
}

/**
 * The program pins and their hover cards.
 *
 * Its own component so it can read the map instance: the card's DIRECTION has
 * to follow the pin's position. Leaflet clips tooltips to the map container
 * (`.leaflet-container` sets overflow:hidden, and no amount of fitBounds
 * padding survives the maxZoom clamp), so a card opening upward from a pin
 * near the top edge loses its first line — the program title. Flipping to
 * "bottom" in the upper half keeps the whole card on the map.
 */
function ProgramMarkers({ programs, teaches, onSelect }) {
  const map = useMap();
  const [centerLat, setCenterLat] = useState(() => map.getCenter().lat);

  useEffect(() => {
    const sync = () => setCenterLat(map.getCenter().lat);
    map.on("moveend", sync);
    map.on("zoomend", sync);
    // Sync once on mount as well as on the events. <FitBounds> sits earlier in
    // the tree, so its effect — and the moveend it triggers — both run BEFORE
    // this subscription exists. Without this the centre stays at the initial
    // continental-US default, every pin reads as "northern", and every card
    // opens downward regardless of where the pin actually is.
    sync();
    return () => {
      map.off("moveend", sync);
      map.off("zoomend", sync);
    };
  }, [map]);

  return (
    <>
      {programs.map((p) => {
          const matches = matchingInstruments(p.instruments, teaches);
          const inTopHalf = p.latitude > centerLat;
          return (
            <Marker
              key={p.program_id}
              position={[p.latitude, p.longitude]}
              icon={PROGRAM_PIN}
              // Hover reveals; clicking commits. Leaflet fires `click` on a
              // marker for touch taps too, so a phone still reaches the page
              // even though it has no hover to show the card with.
              eventHandlers={onSelect ? { click: () => onSelect(p) } : undefined}
            >
              {/* `key` forces a remount when the direction flips. Leaflet takes
                  `direction` as a CONSTRUCTOR option and react-leaflet doesn't
                  re-apply it on a prop change, so an already-bound tooltip keeps
                  whichever direction it was first created with — which, before
                  this, meant every card opened the same way however the map was
                  panned. */}
              <Tooltip
                key={inTopHalf ? "below" : "above"}
                direction={inTopHalf ? "bottom" : "top"}
                offset={inTopHalf ? [0, 10] : [0, -10]}
                opacity={1}
              >
                <div className="min-w-[170px] max-w-[240px]">
                  <div className="font-semibold">{p.title}</div>
                  <div className="text-[12px] opacity-80">{p.org_name}</div>
                  {(p.instruments || []).length > 0 && (
                    <div className="mt-1 text-[12px] opacity-70">{p.instruments.join(", ")}</div>
                  )}
                  {p.compensation && <div className="mt-1 text-[12px] opacity-70">{p.compensation}</div>}
                  {distanceLabel(p.distance_miles) && (
                    <div className="mt-1 text-[12px] opacity-70">{distanceLabel(p.distance_miles)}</div>
                  )}
                  {matches.length > 0 && (
                    <div className="mt-1 text-[12px] font-semibold">Matches {matches.join(", ")}</div>
                  )}
                  {onSelect && <div className="mt-1 text-[12px] opacity-60">Click for details</div>}
                </div>
              </Tooltip>
            </Marker>
          );
      })}
    </>
  );
}
